// dependencies
import React, { ForwardedRef, forwardRef } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Spinner } from "@performyard/py-components";

// components
import Icon from "../Icon";
import Link from "../Link";
// styles
import css from "./Button.module.scss";

interface IAdditionalProps {
	testid?: string;
	target?: "_blank";
	rel?: "noopener noreferrer";
}

export interface BaseButtonProps {
	/**
	 * The content of the button (text or other components)
	 */
	children: React.ReactNode;
	/**
	 * The size of the button
	 * @default medium
	 */
	size?: "large" | "medium" | "small" | "micro";
	/**
	 * The color of the button
	 * @default blue
	 */
	color?: "blue" | "green" | "red" | "gray";
	/**
	 * If we want to render a button without a background
	 */
	textOnly?: boolean;
	/**
	 * if we want to render a link that looks like a button
	 */
	href?: string;
	/**
	 * if we want to render a disabled button
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * If true, this will open a link in a new window. Only applicable if href
	 * is set.
	 */
	isExternal?: boolean;
	/**
	 * if we want to render a button with a spinner
	 * @default false
	 */
	isRequesting?: boolean;
	/**
	 * A font awesome icon to render in the button
	 */
	icon?: string | IconDefinition;
	/**
	 * Any additional styles to apply to the button. It is generally better
	 * to use the className prop to apply styles through CSS.
	 */
	style?: React.CSSProperties;
	/**
	 * A test id to apply to the button. This is useful for testing.
	 */
	testid?: string;
	/**
	 * Any additional classes to apply to the button
	 */
	className?: string;
	/**
	 * The position of the icon relative to the text. Only applicable if icon is set.
	 * @default left
	 */
	iconPosition?: "left" | "right";
	/**
	 * A function to call when the button is clicked
	 */
	onClick?: (e?: Event) => void;

	/**
	 * A function to call when the form is submitted
	 */
	submit?: boolean;

	/**
	 * If the button is hollow or solid
	 */
	isHollow?: boolean;
	/**
	 * If true, the page will hard reload when the link is clicked
	 */
	forceHardReload?: boolean;
}

/**
 * @deprecated Use the Button component from the py-components package instead
 * Primary UI component for user interaction
 */
const Button = forwardRef((props: BaseButtonProps & IAdditionalProps, ref: ForwardedRef<HTMLButtonElement>) => {
	const {
		children,
		size = "medium",
		color = "blue",
		textOnly = false,
		href = null,
		disabled = false,
		isExternal = false,
		isRequesting = false,
		icon = null,
		style = {},
		testid = null,
		className = "",
		iconPosition = "left",
		onClick = () => {},
		submit = false,
		isHollow = false,
		forceHardReload = false,
		...rest
	} = props;

	const buttonClass = `
		${css.button}
		${css[size]} ${css[color]}
		${isHollow ? css.isHollow : null}
		${textOnly ? css.textOnly : null}
		${className}
		${disabled || isRequesting ? css.disabled : null}`;

	let buttonContent = children;

	const additionalProps: IAdditionalProps = { ...rest };

	if (testid) {
		additionalProps["data-testid"] = testid;
	}

	if (isRequesting) {
		buttonContent = <Spinner size={20} color={isHollow ? "neutral-9" : "neutral-0"} data-testid="spinner" />;
	} else if (icon) {
		if (iconPosition === "left") {
			buttonContent = [
				<Icon
					className={`${css.iconWrapper} ${css[`icon-${iconPosition}`]}`}
					key="icon"
					type="fa5"
					name={icon}
					size="small"
				/>,
				<span key="text">{children}</span>,
			];
		}
		if (iconPosition === "right") {
			buttonContent = [
				<span key="text">{children}</span>,
				<Icon
					className={`${css.iconWrapper} ${css[`icon-${iconPosition}`]}`}
					key="icon"
					type="fa5"
					name={icon}
					size="small"
				/>,
			];
		}
	}

	if (href) {
		if (isExternal) {
			additionalProps.target = "_blank";
			additionalProps.rel = "noopener noreferrer";
		}

		return (
			<button
				{...additionalProps}
				className={`${buttonClass} ${css.isLinkButton}`}
				style={style}
				disabled={disabled}
			>
				<Link
					isExternal={isExternal}
					href={href}
					className={`${css.href} no-goal-tooltip`}
					overrideStyles={true}
					forceHardReload={forceHardReload}
				>
					{buttonContent}
				</Link>
			</button>
		);
	}

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (!isRequesting && !disabled && onClick) {
			onClick(event.nativeEvent);
		}
	};

	return (
		<button
			{...additionalProps}
			data-testid={testid}
			disabled={disabled}
			style={style}
			role={submit ? "submit" : "button"}
			onClick={handleClick}
			className={buttonClass}
			type={submit ? "submit" : "button"}
			ref={ref}
		>
			{buttonContent}
		</button>
	);
});
Button.displayName = "Button";

/** @deprecated Use the Button component from the py-components package instead */
export default Button;
